import React, {useEffect, useMemo, useRef, useState} from "react";
import {dia, shapes} from "@joint/core";
import {v4 as uuidv4} from "uuid";
import {IconButton, Tooltip, Typography} from "@mui/material";
import {ZoomIn, ZoomOut, FitScreen} from "@mui/icons-material";
import EnterpriseComponent from "../../eoec/ec/EnterpriseComponent";
import EnterpriseObject from "../../eoec/eo/EnterpriseObject";
// The ADMIN client, not the REST one. This file came from t6, where the REST client points
// at t6 itself. In t1 the t6 endpoints it calls sit behind REACT_APP_ADMIN_API_URL, and the
// REST client would send them to the integrate API, which does not serve them.
// t5, NOT t6. Every read in t6's Administration application requires the administrator
// privilege, and this chart opens from a Stewardship cell any catalog user can see.
import CresAxios from "../../util/CresAxios";
import {V1PositionIDOCToArray} from "../../util/Payload";

type Props = {
    enterpriseObject: EnterpriseObject | undefined;
    /**
     * What the chart is being shown FOR, if it is not the organisation itself. The caption names
     * this instead of the root organisation: rooting the tree at the top of the tree means the root
     * is "Peak Medical" for every position in the company, which answers a question nobody asked.
     */
    captionFor?: string | null;
    /**
     * A position to mark as the one being asked about, if any.
     *
     * On a chart of fifty positions, "where does this owner sit" is still a hunt without it -- the
     * drawer answers the question by drawing the whole company, which is only an answer if you can
     * find the position in it.
     */
    highlightPositionUSID?: string | null;
};

type OrgRow = {
    USID: string;
    Name: string;
    ParentUSID: string | null;
    OrganizationType: string | null;
    Status: string | null;
};

// How many positions one request will carry. Everything the chart draws comes back in this
// single call; past it, the per-organisation path takes over rather than drawing a short tree.
const BULK_POSITION_LIMIT = 5000;

type PositionRow = {
    USID: string;
    Name: string;
    OrganizationUSID: string | null;
    ParentUSID: string | null;
    PrimaryAccountUserName: string | null;
    Status: string | null;
};

type TreeNode = {
    id: string;
    kind: "Organization" | "Position";
    title: string;
    subtitle: string | null;
    inactive: boolean;
    children: TreeNode[];
    width: number;
    height: number;
    subtreeWidth: number;
    x: number;
    y: number;
};

const ORG_WIDTH = 200;
const ORG_HEIGHT = 64;
const POS_WIDTH = 180;
const POS_HEIGHT = 56;
const H_GAP = 24;
const V_GAP = 56;
const PADDING = 32;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1;

// PALE, LIKE EVERYTHING ELSE. Organisations used to be solid blue, which made them the loudest
// thing on a chart opened to find one POSITION. Everything is now an outline and the clicked
// position is the only filled node, so the eye lands on it first and the structure reads as
// background. An organisation is still distinguishable: heavier border, bold label.
// THE SAME FILL t6 ORGANISATIONS USES -- solid blue, white label. Ed's call, and the reason is
// that this IS t6's chart: a reader who knows the org chart from Administration should not have to
// learn a second palette to read the same tree here.
//
// It was paled when this arrived, so the clicked position would be the only filled node on the
// page. That trade is now the other way round: the organisations carry the colour, and the position
// is marked by its ring rather than by being alone in being filled.
const ORG_FILL = "#1498D8";
const ORG_TEXT = "#FFFFFF";
// Pale too. A solid mid-grey was the last filled node besides the clicked position, and a retired
// organisation is the least important thing on the chart -- it should not be the second loudest.
const ORG_INACTIVE_FILL = "#A0A0A0";
const ORG_INACTIVE_STROKE = "#6E6E6E";
const POS_FILL = "#F4F8FB";
const POS_STROKE = "#1498D8";
const POS_TEXT = "#1F3A4D";
const INACTIVE_FILL = "#ECECEC";
const INACTIVE_STROKE = "#9A9A9A";
const INACTIVE_TEXT = "#5E5E5E";
// THE POSITION THE CHART WAS OPENED FOR, and it needs a hue of its own now. It used to be solid
// blue, which read as "the subject" when every organisation was pale -- but the organisations are
// solid blue themselves now, so the one node the reader came to find was the same colour as the
// eighteen boxes above it.
//
// Amber, from the theme's own scale: the complement of blue, so it separates at a glance without
// competing, and not red or green, which would read as a STATUS on a chart that already greys
// inactive nodes. Light fill with dark text rather than a solid warm block -- white on amber is
// under the contrast floor for text this small, and the thick ring does the shouting.
const HIGHLIGHT_FILL = "#FDDC3F";
const HIGHLIGHT_STROKE = "#BC932C";
const HIGHLIGHT_TEXT = "#1F3A4D";
const LINK_COLOR = "#7AA4BD";

function isInactive(status: string | null | undefined): boolean {
    return !!status && status.toLowerCase() !== "active";
}

function readRows<T = any>(ec: EnterpriseComponent | undefined): T[] {
    if (!ec) return [];
    const buffer = ec.getBuffer();
    const out: T[] = [];
    for (const page of buffer.values()) {
        for (const row of page) {
            out.push(Object.fromEntries(row.entries()) as T);
        }
    }
    return out;
}

function currentRow<T = any>(ec: EnterpriseComponent | undefined): T | undefined {
    const row = ec?.getCurrentRecord();
    if (!row) return undefined;
    return Object.fromEntries(row.entries()) as T;
}

function buildTree(rootOrg: OrgRow, orgs: OrgRow[], positions: PositionRow[]): TreeNode {
    const orgsByParent = new Map<string, OrgRow[]>();
    for (const o of orgs) {
        const key = o.ParentUSID ?? "";
        const list = orgsByParent.get(key) ?? [];
        list.push(o);
        orgsByParent.set(key, list);
    }

    const rootPositionsByOrg = new Map<string, PositionRow[]>();
    const childPositionsByParent = new Map<string, PositionRow[]>();
    for (const p of positions) {
        if (p.ParentUSID) {
            const list = childPositionsByParent.get(p.ParentUSID) ?? [];
            list.push(p);
            childPositionsByParent.set(p.ParentUSID, list);
        } else if (p.OrganizationUSID) {
            const list = rootPositionsByOrg.get(p.OrganizationUSID) ?? [];
            list.push(p);
            rootPositionsByOrg.set(p.OrganizationUSID, list);
        }
    }

    const visited = new Set<string>();
    const visitedPositions = new Set<string>();

    function buildOrg(org: OrgRow): TreeNode {
        visited.add(org.USID);
        const childOrgs = (orgsByParent.get(org.USID) ?? []).filter(o => !visited.has(o.USID));
        const rootPositions = rootPositionsByOrg.get(org.USID) ?? [];

        const children: TreeNode[] = [
            ...childOrgs.map(o => buildOrg(o)),
            ...rootPositions.map(p => buildPosition(p)),
        ];

        return {
            id: `org:${org.USID}`,
            kind: "Organization",
            title: org.Name ?? "(unnamed)",
            subtitle: org.OrganizationType ?? null,
            inactive: isInactive(org.Status),
            children,
            width: ORG_WIDTH,
            height: ORG_HEIGHT,
            subtreeWidth: 0,
            x: 0,
            y: 0,
        };
    }

    function buildPosition(p: PositionRow): TreeNode {
        visitedPositions.add(p.USID);
        const childPositions = (childPositionsByParent.get(p.USID) ?? []).filter(c => !visitedPositions.has(c.USID));
        return {
            id: `pos:${p.USID}`,
            kind: "Position",
            title: p.Name ?? "(unnamed)",
            subtitle: "Position",
            inactive: isInactive(p.Status),
            children: childPositions.map(c => buildPosition(c)),
            width: POS_WIDTH,
            height: POS_HEIGHT,
            subtreeWidth: 0,
            x: 0,
            y: 0,
        };
    }

    return buildOrg(rootOrg);
}

function layoutTree(node: TreeNode): {width: number; height: number} {
    function measure(n: TreeNode): number {
        if (n.children.length === 0) {
            n.subtreeWidth = n.width;
            return n.subtreeWidth;
        }
        let total = 0;
        for (let i = 0; i < n.children.length; i++) {
            total += measure(n.children[i]);
            if (i > 0) total += H_GAP;
        }
        n.subtreeWidth = Math.max(n.width, total);
        return n.subtreeWidth;
    }
    measure(node);

    let maxX = 0;
    let maxY = 0;
    function place(n: TreeNode, left: number, top: number) {
        n.x = left + (n.subtreeWidth - n.width) / 2;
        n.y = top;
        maxX = Math.max(maxX, n.x + n.width);
        maxY = Math.max(maxY, n.y + n.height);
        let cursor = left;
        const childTop = top + n.height + V_GAP;
        for (const child of n.children) {
            place(child, cursor, childTop);
            cursor += child.subtreeWidth + H_GAP;
        }
    }
    place(node, 0, 0);
    return {width: maxX, height: maxY};
}

function renderToPaper(node: TreeNode, graph: dia.Graph, highlightId?: string | null) {
    const cells: dia.Cell[] = [];

    function addNode(n: TreeNode): dia.Element {
        const isOrg = n.kind === "Organization";
        const greyed = n.inactive;
        let fill: string;
        let stroke: string;
        let strokeWidth: number;
        let textFill: string;
        if (isOrg) {
            if (greyed) {
                fill = ORG_INACTIVE_FILL;
                stroke = ORG_INACTIVE_STROKE;
                strokeWidth = 1.5;
                // ORG_TEXT again, because the inactive box is solid grey once more rather than pale
                // -- white reads on it. That comment used to say the opposite, and it was right
                // about the pale box it was written for.
                textFill = ORG_TEXT;
            } else {
                fill = ORG_FILL;
                stroke = ORG_FILL;
                strokeWidth = 0;
                textFill = ORG_TEXT;
            }
        } else if (greyed) {
            fill = INACTIVE_FILL;
            stroke = INACTIVE_STROKE;
            strokeWidth = 1.5;
            textFill = INACTIVE_TEXT;
        } else {
            fill = POS_FILL;
            stroke = POS_STROKE;
            strokeWidth = 1.5;
            textFill = POS_TEXT;
        }

        // THE ONE BEING ASKED ABOUT, applied last so it wins over the status colouring above. A
        // retired position that is also the one you clicked should read as "this one" first --
        // whether it is inactive is still legible from its label and from the chart around it.
        const marked = !isOrg && !!highlightId && n.id === `pos:${highlightId}`;
        if (marked) {
            fill = HIGHLIGHT_FILL;
            stroke = HIGHLIGHT_STROKE;
            strokeWidth = 3;
            textFill = HIGHLIGHT_TEXT;
        }
        const rect = new shapes.standard.Rectangle({
            id: n.id,
            position: {x: n.x, y: n.y},
            size: {width: n.width, height: n.height},
            attrs: {
                body: {
                    fill,
                    stroke,
                    strokeWidth,
                    rx: 6,
                    ry: 6,
                },
                label: {
                    text: n.subtitle ? `${n.title}\n${n.subtitle}` : n.title,
                    fill: textFill,
                    fontSize: 13,
                    fontWeight: isOrg || marked ? 600 : 500,
                    fontFamily: "Roboto, Arial, sans-serif",
                    textWrap: {
                        width: n.width - 16,
                        height: n.height - 12,
                        ellipsis: true,
                    },
                },
            },
        });
        cells.push(rect);
        for (const child of n.children) {
            const childEl = addNode(child);
            const link = new shapes.standard.Link({
                source: {id: rect.id},
                target: {id: childEl.id},
                attrs: {
                    line: {
                        stroke: LINK_COLOR,
                        strokeWidth: 1.5,
                        targetMarker: {type: "none"},
                    },
                },
                router: {name: "orthogonal"},
                connector: {name: "rounded"},
            });
            cells.push(link);
        }
        return rect;
    }

    addNode(node);
    graph.resetCells(cells);
}

export default function OrganizationHierarchyView({enterpriseObject, highlightPositionUSID,
                                                  captionFor}: Props) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const paperHostRef = useRef<HTMLDivElement | null>(null);
    const paperRef = useRef<dia.Paper | null>(null);
    const graphRef = useRef<dia.Graph | null>(null);
    const [tick, setTick] = useState(0);

    const orgEC = enterpriseObject?.getEnterpriseComponent("Organization");
    const positionEC = enterpriseObject?.getEnterpriseComponent("OrganizationPosition");

    const [positionsByOrg, setPositionsByOrg] = useState<Map<string, PositionRow[]>>(new Map());
    // Whether the one-call load of every position has finished, and how.
    //
    // STATE, NOT A REF, and the difference is the whole fix. The per-organisation effect runs as
    // soon as the tree first renders -- which is BEFORE the bulk response arrives -- so a ref read
    // at that moment is still false and all 33 requests go out anyway. State re-runs the effect
    // when this resolves, and "pending" makes it wait rather than race.
    const [bulkPositions, setBulkPositions] = useState<"pending" | "loaded" | "failed">("pending");
    const [statusByPositionUSID, setStatusByPositionUSID] = useState<Map<string, string | null>>(new Map());
    const [zoom, setZoom] = useState<number>(1);
    const [containerVersion, setContainerVersion] = useState<number>(0);
    const inFlightRef = useRef<Set<string>>(new Set());
    const allPositionsFetchedRef = useRef<boolean>(false);
    const manualZoomRef = useRef<boolean>(false);
    const lastFittedOrgRef = useRef<string | null>(null);
    const treeRef = useRef<{root: TreeNode; size: {width: number; height: number}} | null>(null);

    const clampZoom = (z: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));

    const computeFitZoom = () => {
        const container = containerRef.current;
        const t = treeRef.current;
        if (!container || !t) return null;
        const cw = container.clientWidth;
        if (cw <= 0) return null;
        const naturalW = t.size.width + PADDING * 2;
        if (naturalW <= 0) return null;
        return clampZoom(Math.min(1, cw / naturalW));
    };

    const onManualZoom = (delta: number) => {
        manualZoomRef.current = true;
        setZoom(z => clampZoom(z + delta));
    };

    const onResetFit = () => {
        manualZoomRef.current = false;
        setContainerVersion(v => v + 1);
    };

    useEffect(() => {
        if (!orgEC && !positionEC) return;
        const bump = () => setTick(t => t + 1);
        orgEC?.addLoadCompletedListener(bump);
        orgEC?.addRecordChangedListener(bump);
        positionEC?.addLoadCompletedListener(bump);
        return () => {
            orgEC?.removeLoadCompletedListener(bump);
            orgEC?.removeRecordChangedListener(bump);
            positionEC?.removeLoadCompletedListener(bump);
        };
    }, [orgEC, positionEC]);

    function fetchAllPositionStatuses() {
        const qbe = btoa(JSON.stringify({
            PlatformMessage: {
                "-idoc": "v1.Position-001",
                "-id": uuidv4(),
                Position: {
                    _attributes: {limit: BULK_POSITION_LIMIT, sort: null, offset: 0},
                    QueryGroups: {
                        QueryGroup: [{
                            Name: "primary",
                            Operator: "AND",
                            Type: "QueryGroup",
                            Membership: {Member: []},
                        }],
                    },
                },
            },
        }));

        CresAxios({
            method: "GET",
            url: `${process.env.REACT_APP_CATALOG_API_URL}/t5/v1/positions`,
            params: {QBE: qbe},
            headers: {"idoc-response": "v1.Position-001"},
        }).then((response: any) => {
            const parsed = V1PositionIDOCToArray(response.data);
            const rows: any[] = parsed?.Results ?? [];
            const map = new Map<string, string | null>();
            for (const r of rows) {
                if (r?.USID) map.set(r.USID, (r.Status ?? null) as string | null);
            }

            // THESE ARE THE POSITIONS, not just their statuses -- v1.Position-001 carries Name,
            // ParentUSID, OrganizationUSID and PrimaryAccountUserName too, which is everything the
            // chart draws. So one call answers the whole question, and the per-organisation fetches
            // below become unnecessary: opening the drawer used to cost THIRTY-SIX requests against
            // Peak's 33 organisations, one apiece plus this one, because the tree asked for each
            // organisation's positions as it rendered it.
            //
            // Kept under the same positionsByOrg map rather than a new piece of state: the tree
            // merges every bucket into one flat map keyed by USID and reads OrganizationUSID off the
            // rows themselves, so the buckets were only ever fetch bookkeeping.
            // NOT IF IT WAS TRUNCATED. The request asks for 5000, so a realm with more positions
            // than that comes back short -- and declaring the bulk load complete would leave those
            // organisations silently empty, which reads as "this department has nobody in it".
            // Falling back to the per-organisation fetches is slower and correct.
            const truncated = rows.length >= BULK_POSITION_LIMIT;
            if (truncated) {
                setBulkPositions("failed");
                // eslint-disable-next-line no-console
                console.warn(`[OrganizationHierarchyView] ${rows.length} positions hit the bulk limit;`
                             + " falling back to per-organisation fetches");
            } else {
                setPositionsByOrg(new Map([["*", rows as PositionRow[]]]));
                setBulkPositions("loaded");
            }
            // eslint-disable-next-line no-console
            console.log("[OrganizationHierarchyView] fetched position statuses:", map.size, "rows");
            setStatusByPositionUSID(map);
        }).catch((err: any) => {
            // eslint-disable-next-line no-console
            console.warn("[OrganizationHierarchyView] failed to fetch position statuses", err);
            // RELEASE THE WAIT. Left pending, the per-organisation fallback would never run and the
            // chart would draw organisations with no positions in them.
            setBulkPositions("failed");
        });
    }

    useEffect(() => {
        if (allPositionsFetchedRef.current) return;
        allPositionsFetchedRef.current = true;
        fetchAllPositionStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function fetchPositionsForOrg(orgUSID: string) {
        if (positionsByOrg.has(orgUSID)) return;
        if (inFlightRef.current.has(orgUSID)) return;
        inFlightRef.current.add(orgUSID);

        // ASKED OF THE POSITIONS SERVICE, not of the organisations service. t6 answers this by
        // returning the organisation with its positions nested inside it; t5 has no organisations
        // service to ask, and does not need one -- its positions carry OrganizationUSID, so the same
        // question is "which positions belong to this organisation", which is a filter.
        const qbe = btoa(JSON.stringify({
            PlatformMessage: {
                "-idoc": "v1.Position-001",
                "-id": uuidv4(),
                Position: {
                    _attributes: {limit: 1000, sort: null, offset: 0},
                    QueryGroups: {
                        QueryGroup: [{
                            Name: "QG1",
                            Type: "QueryGroup",
                            Operator: "AND",
                            Membership: {
                                Member: [{
                                    Type: "Attribute",
                                    Name: "OrganizationUSID",
                                    Value: orgUSID,
                                    Operator: "EQUAL",
                                }],
                            },
                        }],
                    },
                },
            },
        }));

        CresAxios({
            method: "GET",
            url: `${process.env.REACT_APP_CATALOG_API_URL}/t5/v1/positions`,
            params: {QBE: qbe},
            headers: {"idoc-response": "v1.Position-001"},
        }).then((response: any) => {
            const parsed = V1PositionIDOCToArray(response.data);
            const rows: PositionRow[] = (parsed?.Results ?? []) as PositionRow[];
            setPositionsByOrg(prev => {
                const next = new Map(prev);
                next.set(orgUSID, rows);
                return next;
            });
        }).catch(() => {
            setPositionsByOrg(prev => {
                const next = new Map(prev);
                next.set(orgUSID, []);
                return next;
            });
        }).finally(() => {
            inFlightRef.current.delete(orgUSID);
        });
    }

    const tree = useMemo(() => {
        const selected = currentRow<OrgRow>(orgEC);
        if (!selected || !selected.USID) return null;
        const orgs = readRows<OrgRow>(orgEC);

        const merged = new Map<string, PositionRow>();
        for (const row of readRows<PositionRow>(positionEC)) {
            if (row?.USID) merged.set(row.USID, row);
        }
        for (const rows of positionsByOrg.values()) {
            for (const row of rows) {
                if (row?.USID) merged.set(row.USID, row);
            }
        }
        const positions = Array.from(merged.values()).map(p => {
            if (p?.USID && statusByPositionUSID.has(p.USID)) {
                return {...p, Status: statusByPositionUSID.get(p.USID) ?? null};
            }
            return p;
        });

        const root = buildTree(selected, orgs, positions);
        const size = layoutTree(root);
        return {root, size};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgEC, positionEC, tick, positionsByOrg, statusByPositionUSID]);

    useEffect(() => {
        if (!tree) return;
        const orgUSIDs: string[] = [];
        function collect(n: TreeNode) {
            if (n.kind === "Organization") {
                orgUSIDs.push(n.id.replace(/^org:/, ""));
            }
            for (const c of n.children) collect(c);
        }
        collect(tree.root);
        // WAIT FOR THE ONE CALL before asking 33 times. It carries every position in the realm, so
        // there is nothing left to ask; the per-organisation path below runs only if it failed or
        // came back truncated, because a chart missing its positions is worse than the requests.
        if (bulkPositions === "pending" || bulkPositions === "loaded") return;

        for (const usid of orgUSIDs) {
            if (!positionsByOrg.has(usid) && !inFlightRef.current.has(usid)) {
                fetchPositionsForOrg(usid);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, bulkPositions]);

    useEffect(() => {
        if (!paperHostRef.current) return;
        if (graphRef.current) return;
        const graph = new dia.Graph({}, {cellNamespace: shapes});
        const paper = new dia.Paper({
            el: paperHostRef.current,
            model: graph,
            width: 100,
            height: 100,
            background: {color: "#FFFFFF"},
            interactive: false,
            cellViewNamespace: shapes,
            gridSize: 1,
        });
        graphRef.current = graph;
        paperRef.current = paper;
        return () => {
            paper.remove();
            graphRef.current = null;
            paperRef.current = null;
        };
    }, []);

    useEffect(() => {
        treeRef.current = tree;
        const graph = graphRef.current;
        const paper = paperRef.current;
        if (!graph || !paper) return;
        if (!tree) {
            graph.clear();
            paper.setDimensions(100, 100);
            return;
        }

        const selected = currentRow<OrgRow>(orgEC);
        const usid = selected?.USID ?? null;
        if (usid && usid !== lastFittedOrgRef.current) {
            manualZoomRef.current = false;
            lastFittedOrgRef.current = usid;
        }

        let effectiveZoom = zoom;
        if (!manualZoomRef.current) {
            const fit = computeFitZoom();
            if (fit !== null) effectiveZoom = fit;
        }

        renderToPaper(tree.root, graph, highlightPositionUSID);
        paper.setDimensions(
            tree.size.width + PADDING * 2,
            tree.size.height + PADDING * 2,
        );
        paper.scale(1, 1);
        paper.translate(PADDING, PADDING);

        if (effectiveZoom !== zoom) setZoom(effectiveZoom);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, zoom, orgEC, containerVersion, highlightPositionUSID]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const onChange = () => setContainerVersion(v => v + 1);
        const ro = new ResizeObserver(onChange);
        ro.observe(container);
        window.addEventListener("resize", onChange);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", onChange);
        };
    }, []);

    const selectedRow = currentRow<OrgRow>(orgEC);
    const hasSelection = !!selectedRow;
    const selectedOrgName = selectedRow?.Name ?? "";

    return (
        <div style={{display: "flex", flexDirection: "column", margin: "30px 0 30px 0", minWidth: 0}}>
            {!hasSelection && (
                <div style={{padding: "40px", textAlign: "center", color: "#7AA4BD"}}>
                    Select an organization in the table above to see its hierarchy.
                </div>
            )}
            <div style={{display: hasSelection ? "flex" : "none", flexDirection: "column", minWidth: 0}}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 12px 6px 16px",
                        border: "1px solid #E1ECF2",
                        borderBottom: "none",
                        borderTopLeftRadius: 6,
                        borderTopRightRadius: 6,
                        backgroundColor: "#F4F8FB",
                        minHeight: 44,
                        boxSizing: "border-box",
                    }}
                >
                    <Typography style={{fontSize: 16, fontWeight: 500, color: "#1F3A4D"}}>
                        Hierarchy for {captionFor || selectedOrgName}
                    </Typography>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                        <Tooltip title="Zoom out">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onManualZoom(-ZOOM_STEP)}
                                    disabled={zoom <= ZOOM_MIN + 1e-6}
                                >
                                    <ZoomOut/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Typography
                            style={{
                                fontSize: 13,
                                color: "#1F3A4D",
                                minWidth: 44,
                                textAlign: "center",
                                userSelect: "none",
                            }}
                        >
                            {Math.round(zoom * 100)}%
                        </Typography>
                        <Tooltip title="Zoom in">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => onManualZoom(ZOOM_STEP)}
                                    disabled={zoom >= ZOOM_MAX - 1e-6}
                                >
                                    <ZoomIn/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Fit to width">
                            <IconButton size="small" onClick={onResetFit}>
                                <FitScreen/>
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
                <div
                    ref={containerRef}
                    style={{
                        width: "100%",
                        minHeight: 600,
                        border: "1px solid #E1ECF2",
                        borderBottomLeftRadius: 6,
                        borderBottomRightRadius: 6,
                        overflowX: "auto",
                        overflowY: "auto",
                        backgroundColor: "#FFFFFF",
                        boxSizing: "border-box",
                    }}
                >
                    <div
                        style={{
                            width: tree ? (tree.size.width + PADDING * 2) * zoom : 0,
                            height: tree ? (tree.size.height + PADDING * 2) * zoom : 0,
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            ref={paperHostRef}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                transform: `scale(${zoom})`,
                                transformOrigin: "top left",
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
